import { Button, Card, Stack, Typography } from '@mui/material';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFUpload } from 'src/components/hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { editCandidateDetails } from 'src/services/candidate.service';
import { getOrganizationId } from 'src/services/user.service';
import { useAuthContext } from 'src/auth/hooks';
import { useParams } from 'react-router';
import { uploadResumeToS3 } from 'src/services/aws.service';
import { enqueueSnackbar } from 'notistack';
import Iconify from 'src/components/iconify';

const extensionMap = {
  doc: ['docx', 'doc'],
  pdf: ['pdf'],
};

const ResumeViewer = ({ resumeUrl }) => {
  const urlParts = resumeUrl.split('.');
  const lastExtension = urlParts[urlParts.length - 1];

  if (extensionMap.doc.includes(lastExtension)) {
    return (
      <iframe
        title="doc"
        className="doc"
        style={{
          width: '100%',
          height: 800,
          borderRadius: '5px',
        }}
        src={`https://docs.google.com/gview?url=${resumeUrl}&embedded=true`}
      />
    );
  }
  if (extensionMap.pdf.includes(lastExtension)) {
    return (
      <object
        style={{ borderRadius: '5px' }}
        data={`${resumeUrl}`}
        width="100%"
        height="800"
        aria-label="resume"
      />
    );
  }
  return null;
};

const Profile = ({ candidateDetails, setCandidateChanged }) => {
  // variables
  const params = useParams();
  const { jobId } = params;
  const { user } = useAuthContext();
  const organization_id = getOrganizationId(user);

  // states
  const [resumeUrl, setResumeUrl] = useState('');

  // memo
  const defaultValues = useMemo(
    () => ({
      coverUrl: null,
    }),
    [],
  );

  const Schema = Yup.object().shape({
    coverUrl: Yup.mixed().nullable(),
  });

  const CandidateUpdateMethods = useForm({
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const [methods, setMethods] = useState(CandidateUpdateMethods);

  // variables
  const { reset, setValue, handleSubmit } = methods;

  const handleSingleDrop = useCallback(
    acceptedFiles => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('coverUrl', newFile, { shouldValidate: true });
      }
    },
    [setValue],
  );

  const handleRemoveFile = inputFile => {
    setValue('coverUrl', null);
  };

  const submitHandler = handleSubmit(async data => {
    try {
      const resume_url = await uploadResumeToS3(data.coverUrl);
      const reqBody = {};

      if (data.coverUrl) reqBody.resume_url = resume_url;

      const res = await editCandidateDetails(
        organization_id,
        params.candidateId,
        reqBody,
      );

      if (res.success) {
        setCandidateChanged(prev => !prev);
        enqueueSnackbar('Updated successfully...');
      }
    } catch (err) {
      console.log(err);
    }
  });

  const convertFileName = text => text.toLowerCase().replace(/\s+/g, '-');

  const downloadResume = async () => {
    if (resumeUrl) {
      // const response = await fetch(resumeUrl);
      // const blob = await response.blob();
      // const link = document.createElement('a');
      // link.href = URL.createObjectURL(blob);
      // link.setAttribute(
      //   'download',
      //   `${
      //     jobId
      //       ? convertFileName(candidateDetails.candidate_info.name)
      //       : convertFileName(candidateDetails.name)
      //   }-resume`,
      // );
      // document.body.appendChild(link);
      // link.click();
      // document.body.removeChild(link);

      const link = document.createElement('a');
      link.href = resumeUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // effects
  useEffect(() => {
    if (jobId) {
      setResumeUrl(candidateDetails.candidate_info.resume_url);
    } else {
      setResumeUrl(candidateDetails.resume_url);
    }
  }, [candidateDetails, jobId]);

  return (
    <Stack spacing={3}>
      {jobId && candidateDetails.candidate_info.profile_blob && (
        <Card sx={{ p: 3 }}>
          <Stack spacing={3}>
            {candidateDetails.candidate_info.profile_blob
              .split('\n\n')
              .map((section, index) => (
                <Stack key={index}>
                  {section.split('\n').map((line, i) => (
                    <Typography variant="body1" key={i}>
                      {line}
                    </Typography>
                  ))}
                </Stack>
              ))}
          </Stack>
        </Card>
      )}
      <Card sx={{ p: 3 }}>
        {resumeUrl ? (
          <Stack spacing={2}>
            <Button
              variant="outlined"
              onClick={downloadResume}
              sx={{
                alignSelf: 'flex-end',
              }}
              startIcon={<Iconify icon="material-symbols:download" />}
            >
              Download Resume
            </Button>
            <ResumeViewer resumeUrl={resumeUrl} />
          </Stack>
        ) : (
          <FormProvider
            methods={methods}
            onSubmit={handleSubmit(submitHandler)}
          >
            <Stack>
              <RHFUpload
                name="coverUrl"
                maxSize={5000000}
                onRemove={handleRemoveFile}
                onDrop={handleSingleDrop}
                accept={{
                  'application/pdf': ['.pdf'],
                  'application/msword': ['.doc'],
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                    ['docx'],
                }}
              />
              <Button
                sx={{ width: '5rem', margin: '0px auto', marginTop: '4rem' }}
                type="submit"
                variant="contained"
              >
                Save
              </Button>
            </Stack>
          </FormProvider>
        )}
      </Card>
    </Stack>
  );
};

export default Profile;

ResumeViewer.propTypes = {
  resumeUrl: PropTypes.string,
};

Profile.propTypes = {
  candidateDetails: PropTypes.object,
  setCandidateChanged: PropTypes.func,
};
